import { useIsIOS } from "@design-system/hooks";
import { motion } from "framer-motion";
import { useEffect, useState } from "react";

const DIGIT_COUNT = 10;
const STEP_PERCENT = 100 / DIGIT_COUNT;

// Matches text-4xl leading-none / lg:text-5xl leading-none
const containerClass = "h-[36px] w-[22px] lg:h-[48px] lg:w-[29px]";
const textClass = "text-4xl lg:text-5xl";
const dotClass = "h-[36px] w-[14px] lg:h-[48px] lg:w-[18px]";

function ScrollDigit({
  digit,
  active,
  char,
}: {
  digit: number;
  active: boolean;
  char?: string;
}) {
  const isIOS = useIsIOS();
  const nums = Array.from({ length: DIGIT_COUNT }, (_, i) => i);

  if (char) {
    return (
      <div
        className={`flex ${dotClass} items-end justify-center font-bold leading-none text-white`}
      >
        <span className={`${textClass} leading-none`}>{char}</span>
      </div>
    );
  }

  if (isIOS) {
    return (
      <div
        className={`flex ${containerClass} items-center justify-center ${textClass} font-bold leading-none text-white`}
        style={{ fontVariantNumeric: "tabular-nums" }}
      >
        {digit}
      </div>
    );
  }

  return (
    <div className={`${containerClass} overflow-hidden`}>
      <motion.div
        initial={{ y: "0%" }}
        animate={{ y: `-${active ? digit * STEP_PERCENT : 0}%` }}
        transition={{ duration: 2.5, ease: [0.22, 1, 0.36, 1] }}
        style={{ willChange: "transform" }}
        className="flex flex-col"
      >
        {nums.map((n) => (
          <div
            key={n}
            className={`flex ${containerClass} items-center justify-center ${textClass} font-bold leading-none text-white`}
            style={{ fontVariantNumeric: "tabular-nums" }}
            aria-hidden
          >
            {n}
          </div>
        ))}
      </motion.div>
    </div>
  );
}

export function CountingNumber({
  number,
  isInView,
}: {
  number: string;
  isInView: boolean;
}) {
  const [elements, setElements] = useState<
    Array<{ type: "digit" | "char"; value: number | string }>
  >([]);

  useEffect(() => {
    const parsed: Array<{ type: "digit" | "char"; value: number | string }> =
      [];
    for (const char of number.split("")) {
      if (/[0-9]/.test(char)) {
        parsed.push({ type: "digit", value: Number(char) });
      } else {
        parsed.push({ type: "char", value: char });
      }
    }
    setElements(parsed);
  }, [number]);

  return (
    <div className="flex items-end" dir="ltr">
      {elements.map((el, i) => (
        <ScrollDigit
          key={i}
          digit={el.type === "digit" ? (el.value as number) : 0}
          active={isInView}
          char={el.type === "char" ? (el.value as string) : undefined}
        />
      ))}
    </div>
  );
}
